/**
 * Bundle widget v20 — Mobile Teleportation & UI Fixes
 */
(function () {
  const STAR_COLOR = '#fbbf24';
  const STAR_EMPTY = '#e5e7eb';

  class BundleWidget extends HTMLElement {
    constructor() {
      super();
      this.discountPercent = parseFloat(this.dataset.discountPercent || '0');
      this.hasDiscount = this.dataset.hasDiscount === 'true';
      this.accessories = {};
      this._carousels = new Map();
      this._onActionClick = this.handleActionClick.bind(this);
      this._submitting = false;
    }

    connectedCallback() {
      // 1. ALWAYS bind the global click listener (crucial for when the element moves on mobile)
      document.addEventListener('click', this._onActionClick, true);

      // 2. Safeguard: Prevent double-firing when DOM Teleportation moves the widget
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.summaryLabelTemplate = this.dataset.summaryLabel || '';
      this.saveTemplate = this.dataset.saveText || '';
      this.checkoutMicrocopy = this.dataset.checkoutMicrocopy || '';
      this.variantErrorText = this.dataset.variantErrorText || '';
      this.genericErrorText = this.dataset.genericErrorText || '';
      this.notifyMeText = this.dataset.notifyMeText || '';
      this.expandLabel = 'Show accessory details';
      this.collapseLabel = 'Hide accessory details';

      this.parseVariantDiscounts();
      this.parseAccessoryData();
      this.computeRatings();
      this.bindEvents();
      this.cacheMainButtonRefs();

      ['on:variant:change', 'variant:change', 'variant:changed', 'variant:updated'].forEach((evt) => {
        document.addEventListener(evt, (e) => {
          const id = this.extractVariantId(e);
          if (id) {
            this.updateDiscountForVariant(id);
            this.switchVariantGroup(id);
          }
        });
      });

      const initialVariantId = this.getMainVariantId();
      if (initialVariantId) {
        this.switchVariantGroup(initialVariantId);
      }

      this.recalculate();
      
      // Initialize the dynamic mobile relocation script
      this.setupMobileTeleport();
    }

    disconnectedCallback() {
      // Clean up the listener when the element is removed or moved
      document.removeEventListener('click', this._onActionClick, true);
    }

    extractVariantId(e) {
      if (!e) return null;
      if (e.detail) {
        const v = e.detail.variant || e.detail || {};
        const id = v.id || v.variantId || v.variant_id || null;
        if (!id && typeof e.detail === 'number') return String(e.detail);
        if (id) return String(id);
      }
      if (e.target && e.target.value) return String(e.target.value);
      return null;
    }

    parseVariantDiscounts() {
      const script = this.querySelector('script[data-variant-discounts]');
      if (!script) return;
      try {
        this.variantDiscountsMap = JSON.parse(script.textContent);
      } catch (err) {
        this.variantDiscountsMap = {};
      }
    }

    updateDiscountForVariant(variantId) {
      if (!this.variantDiscountsMap) return;
      const newDiscount = this.variantDiscountsMap[variantId];
      if (newDiscount !== undefined) {
        this.discountPercent = parseFloat(newDiscount) || 0;
        this.hasDiscount = this.discountPercent > 0;
        this.dataset.discountPercent = this.discountPercent;
        this.dataset.hasDiscount = String(this.hasDiscount);

        const headerEl = this.querySelector('[data-bundle-header]');
        if (headerEl) {
          const headingTemplate = headerEl.dataset.headingTemplate || 'Complete your bundle — [discount]% off accessories';
          const headingNoDesc = headerEl.dataset.headingNodesc || 'Buy it with';
          const span = headerEl.querySelector('span');
          if (span) {
            span.textContent = this.hasDiscount ? headingTemplate.replace('[discount]', this.discountPercent) : headingNoDesc;
          }
          headerEl.classList.toggle('bundle-widget__header--no-discount', !this.hasDiscount);
        }

        Object.keys(this.accessories).forEach((rowId) => {
          this.updatePriceDisplay(rowId);
        });

        this.recalculate();
      }
    }

    switchVariantGroup(variantId) {
      const groups = this.querySelectorAll('[data-bundle-variant-group]');
      if (groups.length === 0) return;

      let hasVisibleGroup = false;
      groups.forEach(group => {
        if (String(group.dataset.variantId) === String(variantId)) {
          group.hidden = false;
          hasVisibleGroup = true;
          const carousels = group.querySelectorAll('[data-accessory-carousel]');
          carousels.forEach(c => this.initCarousel(c));
        } else {
          group.hidden = true;
        }
      });

      // Toggles the entire widget element instead of just the inner frame
      this.style.display = hasVisibleGroup ? 'block' : 'none';

      this.syncSelectAll();
      this.recalculate();
    }

    cacheMainButtonRefs() {
      this.mainFormId = this.dataset.mainFormId || null;
      this.stickyFormId = this.dataset.stickyFormId || null;

      const mainForm = this.mainFormId ? document.getElementById(this.mainFormId) : null;
      const stickyForm = this.stickyFormId ? document.getElementById(this.stickyFormId) : null;

      this.mainForm = mainForm;
      this.stickyForm = stickyForm;
      this.mainAddBtn = mainForm ? mainForm.querySelector('[name="add"]') : null;
      this.stickyAddBtn = stickyForm ? stickyForm.querySelector('[name="add"]') : null;
      this.buyNowBtn = document.getElementById('buy-now');

      [this.mainAddBtn, this.stickyAddBtn].forEach((btn) => {
        if (btn && !btn.dataset.addToCartText) {
          btn.dataset.addToCartText = btn.textContent.trim();
        }
      });
      if (this.buyNowBtn) {
        this.buyNowBaseText = this.buyNowBtn.textContent.trim();
      }
    }

    handleActionClick(event) {
      const clickedBtn = event.target.closest('button');
      if (!clickedBtn) return;
      if (clickedBtn !== this.mainAddBtn && clickedBtn !== this.stickyAddBtn && clickedBtn !== this.buyNowBtn) return;
      if (this.getCheckedAccessoryCount() === 0) return;

      if (this._submitting) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this.addBundle({ redirectToCheckout: clickedBtn === this.buyNowBtn, triggerBtn: clickedBtn });
    }

    async addBundle({ redirectToCheckout, triggerBtn }) {
      const errorEl = this.querySelector('[data-bundle-error]');
      if (errorEl) {
        errorEl.hidden = true;
        errorEl.textContent = '';
      }

      const items = this.buildBundleItems();
      if (!items || items.length <= 1) {
        if (errorEl) {
          errorEl.hidden = false;
          errorEl.textContent = this.variantErrorText;
        }
        return;
      }

      this._submitting = true;

      const allButtons = [this.mainAddBtn, this.stickyAddBtn, this.buyNowBtn].filter(Boolean);
      allButtons.forEach((b) => {
        b.setAttribute('aria-disabled', 'true');
        if (b === triggerBtn) b.classList.add('is-loading');
      });

      const ProductFormClass = customElements.get('product-form');
      let succeeded = false;

      try {
        const data = await ProductFormClass.submitItems(items);
        succeeded = true;

        if (redirectToCheckout) {
          window.location.href = '/checkout';
          return;
        }

        ProductFormClass.updateCartIcon(data);

        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          cartDrawer.renderContents(data, theme.settings.afterAtc === 'drawer');
        } else if (theme.settings.afterAtc === 'page') {
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        }
      } catch (err) {
        if (errorEl) {
          errorEl.hidden = false;
          errorEl.textContent = (err && (err.cartError || err.message)) || this.genericErrorText;
        }
        document.dispatchEvent(
          new CustomEvent('on:cart:error', { bubbles: true, detail: { error: errorEl ? errorEl.textContent : null } })
        );
      } finally {
        this._submitting = false;
        allButtons.forEach((b) => {
          if (b === triggerBtn && succeeded) b.classList.add('is-success');
          b.removeAttribute('aria-disabled');
          setTimeout(() => {
            b.classList.remove('is-loading');
            b.classList.remove('is-success');
          }, 1400);
        });
        this.updateButtonLabels(this.getCheckedAccessoryCount());
      }
    }

    getMainVariantId() {
      const input = this.mainForm ? this.mainForm.querySelector('[name="id"]') : null;
      return input ? input.value : null;
    }

    getMainQuantity() {
      const qtyInput = this.mainForm ? this.mainForm.querySelector('[name="quantity"]') : null;
      const qty = qtyInput ? parseInt(qtyInput.value, 10) : 1;
      return qty > 0 ? qty : 1;
    }

    buildBundleItems() {
      const mainVariantId = this.getMainVariantId();
      if (!mainVariantId) return null;

      const items = [{ id: mainVariantId, quantity: this.getMainQuantity() }];

      Object.values(this.accessories).forEach((accessory) => {
        const card = this.querySelector(`[data-accessory-card][data-accessory-id="${accessory.rowId}"]`);
        const group = card ? card.closest('[data-bundle-variant-group]') : null;
        const isVisible = group ? !group.hidden : true;

        if (isVisible && accessory.checked && accessory.currentVariant) {
          items.push({ id: accessory.currentVariant.id, quantity: 1 });
        }
      });
      return items;
    }

    parseAccessoryData() {
      const cards = this.querySelectorAll('[data-accessory-card]');
      cards.forEach((card) => {
        const rowId = card.dataset.accessoryId;
        const dataScript = this.querySelector(`script[data-accessory-variants="${rowId}"]`);
        if (!dataScript) return;

        let data;
        try {
          data = JSON.parse(dataScript.textContent);
        } catch (e) {
          return;
        }

        const pinnedVariantId = dataScript.getAttribute('data-pinned-variant-id') || data.initialVariantId;
        let matchedVariant = null;

        const selects = Array.from(this.querySelectorAll(`[data-accessory-select][data-accessory-id="${rowId}"]`));

        if (pinnedVariantId) {
          matchedVariant = data.variants.find((v) => String(v.id) === String(pinnedVariantId));
          if (matchedVariant && matchedVariant.options) {
            selects.forEach((select) => {
              const idx = parseInt(select.dataset.optionIndex, 10);
              if (matchedVariant.options[idx] !== undefined) {
                select.value = matchedVariant.options[idx];
                if (select.parentNode && select.parentNode.classList.contains('bundle-widget__select-wrapper')) {
                   select.parentNode.dataset.value = select.options[select.selectedIndex]?.text || '';
                }
              }
            });
          }
        }

        if (!matchedVariant && selects.length > 0) {
          let selectedVals = [];
          selects.forEach((select) => {
            selectedVals[parseInt(select.dataset.optionIndex, 10)] = select.value;
          });
          matchedVariant = data.variants.find((v) => v.available && v.options.every((val, i) => val === selectedVals[i]));
        }

        if (!matchedVariant) {
          matchedVariant = data.variants.find((v) => v.available) || data.variants[0];
          if (matchedVariant && matchedVariant.options) {
            selects.forEach((select) => {
              const idx = parseInt(select.dataset.optionIndex, 10);
              if (matchedVariant.options[idx] !== undefined) {
                select.value = matchedVariant.options[idx];
                if (select.parentNode && select.parentNode.classList.contains('bundle-widget__select-wrapper')) {
                   select.parentNode.dataset.value = select.options[select.selectedIndex]?.text || '';
                }
              }
            });
          }
        }

        this.accessories[rowId] = {
          rowId: rowId,
          available: data.available,
          variants: data.variants,
          selectedOptions: matchedVariant ? matchedVariant.options.slice() : [],
          checked: false,
          currentVariant: matchedVariant || null,
        };

        this.updatePriceDisplay(rowId);
      });
    }

syncSelectAll() {
      const selectAllCb = this.querySelector('[data-select-all-checkbox]');
      const selectAllRow = this.querySelector('[data-bundle-select-all]');
      if (!selectAllCb) return;

      // Bulletproof collection: explicitly check group visibility using JS instead of CSS selectors
      let checkboxes = [];
      this.querySelectorAll('[data-accessory-checkbox]').forEach((cb) => {
        const group = cb.closest('[data-bundle-variant-group]');
        const isGroupVisible = group ? !group.hidden : true;
        
        // Only count the item if its parent group is currently visible AND the item is in-stock
        if (isGroupVisible && !cb.hidden) {
          checkboxes.push(cb);
        }
      });

      // Use !important to completely override any flexbox CSS
      if (selectAllRow) {
        if (checkboxes.length <= 1) {
          selectAllRow.style.setProperty('display', 'none', 'important');
        } else {
          selectAllRow.style.removeProperty('display');
        }
      }

      if (checkboxes.length === 0) {
        selectAllCb.checked = false;
        return;
      }

      const allChecked = checkboxes.every((cb) => cb.checked);
      selectAllCb.checked = allChecked;
    }

    bindEvents() {
      const selectAllCb = this.querySelector('[data-select-all-checkbox]');
      if (selectAllCb) {
        selectAllCb.addEventListener('change', (e) => {
          const isChecked = e.target.checked;

          this.querySelectorAll('[data-accessory-checkbox]').forEach((cb) => {
            const group = cb.closest('[data-bundle-variant-group]');
            const isVisible = group ? !group.hidden : true;

            if (isVisible && !cb.hidden && !cb.disabled) {
              cb.checked = isChecked;
              const rowId = cb.dataset.accessoryId;
              if (this.accessories[rowId]) {
                this.accessories[rowId].checked = isChecked;
              }
            }
          });

          this.recalculate();
        });
      }

      this.querySelectorAll('[data-accessory-checkbox]').forEach((cb) => {
        cb.addEventListener('change', (e) => {
          const rowId = e.target.dataset.accessoryId;
          if (this.accessories[rowId]) {
            this.accessories[rowId].checked = e.target.checked;
            this.syncSelectAll();
            this.recalculate();
          }
        });
      });

      this.querySelectorAll('[data-accessory-select]').forEach((select) => {
        if (select.parentNode && select.parentNode.classList.contains('bundle-widget__select-wrapper')) {
           select.parentNode.dataset.value = select.options[select.selectedIndex]?.text || '';
        }

        select.addEventListener('change', (e) => {
          const rowId = e.target.dataset.accessoryId;
          const optIndex = parseInt(e.target.dataset.optionIndex, 10) || 0;
          this.selectOption(rowId, optIndex, e.target.value);

          if (e.target.parentNode && e.target.parentNode.classList.contains('bundle-widget__select-wrapper')) {
             e.target.parentNode.dataset.value = e.target.options[e.target.selectedIndex]?.text || '';
          }
        });
      });

      this.querySelectorAll('[data-accessory-expand-toggle]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const rowId = btn.dataset.accessoryId;
          const panel = this.querySelector(`[data-accessory-panel][data-accessory-id="${rowId}"]`);
          if (!panel) return;

          const isOpen = btn.getAttribute('aria-expanded') === 'true';
          if (isOpen) {
            panel.classList.remove('is-open');
            btn.setAttribute('aria-expanded', 'false');
            btn.setAttribute('aria-label', this.expandLabel);
            window.setTimeout(() => {
              if (!panel.classList.contains('is-open')) panel.hidden = true;
            }, 300);
          } else {
            panel.hidden = false;
            panel.offsetHeight;
            panel.classList.add('is-open');
            btn.setAttribute('aria-expanded', 'true');
            btn.setAttribute('aria-label', this.collapseLabel);

            const carousel = panel.querySelector('[data-accessory-carousel]');
            if (carousel) {
              this.initCarousel(carousel);
            }
          }
        });
      });

      this.querySelectorAll('[data-notify-btn]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const rowId = btn.dataset.accessoryId;
          this.dispatchEvent(
            new CustomEvent('bundle:notify-me-click', {
              bubbles: true,
              detail: { rowId: rowId },
            })
          );
        });
      });
    }

    initCarousel(carousel) {
      const rowId = carousel.dataset.accessoryId || carousel.getAttribute('data-accessory-id');
      const track = carousel.querySelector('[data-carousel-track]');
      const slides = carousel.querySelectorAll('[data-carousel-slide]');
      const prevBtn = carousel.querySelector('[data-carousel-prev]');
      const nextBtn = carousel.querySelector('[data-carousel-next]');
      const dots = carousel.querySelectorAll('[data-carousel-dot]');
      if (!track || slides.length === 0) return;

      let state = this._carousels.get(rowId);
      if (!state) {
        state = { index: 0 };
        this._carousels.set(rowId, state);

        const render = () => {
          track.style.transform = `translateX(-${state.index * 100}%)`;
          dots.forEach((dot, i) => dot.classList.toggle('is-active', i === state.index));
        };

        const goTo = (i) => {
          state.index = (i + slides.length) % slides.length;
          render();
        };

        if (prevBtn) prevBtn.addEventListener('click', () => goTo(state.index - 1));
        if (nextBtn) nextBtn.addEventListener('click', () => goTo(state.index + 1));
        dots.forEach((dot) => {
          dot.addEventListener('click', () => goTo(parseInt(dot.dataset.index, 10) || 0));
        });

        let touchStartX = null;
        track.addEventListener(
          'touchstart',
          (e) => {
            touchStartX = e.touches[0].clientX;
          },
          { passive: true }
        );
        track.addEventListener(
          'touchend',
          (e) => {
            if (touchStartX === null) return;
            const delta = e.changedTouches[0].clientX - touchStartX;
            if (Math.abs(delta) > 40) goTo(delta < 0 ? state.index + 1 : state.index - 1);
            touchStartX = null;
          },
          { passive: true }
        );

        state.render = render;
        state.goTo = goTo;
      }

      state.index = Math.min(state.index, slides.length - 1);
      state.render();
    }

    computeRatings() {
      this.querySelectorAll('[data-accessory-card]').forEach((card) => {
        const rowId = card.dataset.accessoryId;

        const badgeSlot = this.querySelector(`[data-jdgm-badge-slot][data-accessory-id="${rowId}"]`);
        const badge = badgeSlot ? badgeSlot.querySelector('.jdgm-prev-badge') : null;

        let internalAvg = 0;
        let internalCount = 0;
        if (badge) {
          internalAvg = parseFloat(badge.getAttribute('data-average-rating')) || 0;
          internalCount = parseInt(badge.getAttribute('data-number-of-reviews'), 10) || 0;
        }

        const ratingEls = this.querySelectorAll(`[data-accessory-rating][data-accessory-id="${rowId}"]`);
        if (ratingEls.length === 0) return;

        const externalAvg = parseFloat(ratingEls[0].dataset.externalAvg) || 0;
        const externalCount = parseInt(ratingEls[0].dataset.externalCount, 10) || 0;

        if (internalCount === 0 && externalCount === 0) return;

        let totalWeighted = 0;
        let totalCount = 0;
        if (internalCount > 0) {
          totalWeighted += internalAvg * internalCount;
          totalCount += internalCount;
        }
        if (externalCount > 0) {
          totalWeighted += externalAvg * externalCount;
          totalCount += externalCount;
        }

        const combinedAvg = totalWeighted / totalCount;
        const roundedStars = Math.round(combinedAvg);

        let starsHTML = '';
        for (let i = 1; i <= 5; i++) {
          starsHTML += `<svg viewBox="0 0 24 24" fill="${i <= roundedStars ? STAR_COLOR : STAR_EMPTY}" aria-hidden="true"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>`;
        }

        ratingEls.forEach((el) => {
          const starsEl = el.querySelector('[data-rating-stars]');
          const countEl = el.querySelector('[data-rating-count]');
          if (starsEl) starsEl.innerHTML = starsHTML;
          if (countEl) countEl.textContent = `${combinedAvg.toFixed(1)} (${totalCount})`;
          el.hidden = false;
        });
      });
    }

    selectOption(rowId, optionIndex, value) {
      const accessory = this.accessories[rowId];
      if (!accessory) return;

      accessory.selectedOptions[optionIndex] = value;

      const match = accessory.variants.find((v) =>
        v.options.every((optVal, i) => optVal === accessory.selectedOptions[i])
      );

      if (match) {
        accessory.currentVariant = match;
      } else {
        accessory.currentVariant = { ...accessory.currentVariant, available: false };
      }

      this.updatePriceDisplay(rowId);
      this.recalculate();
    }

    updatePriceDisplay(rowId) {
      const accessory = this.accessories[rowId];
      if (!accessory || !accessory.currentVariant) return;

      const variant = accessory.currentVariant;

      const card = this.querySelector(`[data-accessory-card][data-accessory-id="${rowId}"]`);
      const checkbox = this.querySelector(`[data-accessory-checkbox][data-accessory-id="${rowId}"]`);
      const slash = this.querySelector(`[data-accessory-soldout-icon][data-accessory-id="${rowId}"]`);
      const inStockUI = this.querySelector(`[data-accessory-instock-ui][data-accessory-id="${rowId}"]`);
      const soldOutUI = this.querySelector(`[data-accessory-soldout-ui][data-accessory-id="${rowId}"]`);
      const expandToggle = this.querySelector(`[data-accessory-expand-toggle][data-accessory-id="${rowId}"]`);
      const panel = this.querySelector(`[data-accessory-panel][data-accessory-id="${rowId}"]`);

      if (variant.available) {
        if (card) card.classList.remove('bundle-widget__card--soldout');
        if (checkbox) checkbox.hidden = false;
        if (slash) slash.hidden = true;
        if (soldOutUI) soldOutUI.hidden = true;
        if (inStockUI) {
          inStockUI.hidden = false;
          const priceBlock = inStockUI.querySelector('[data-accessory-price]');
          if (priceBlock) priceBlock.hidden = false;
        }
        if (expandToggle) expandToggle.hidden = false;
      } else {
        if (card) card.classList.add('bundle-widget__card--soldout');
        if (checkbox) {
          checkbox.hidden = true;
          checkbox.checked = false;
          accessory.checked = false;
        }
        if (slash) slash.hidden = false;
        if (soldOutUI) soldOutUI.hidden = false;
        
        if (inStockUI) {
          inStockUI.hidden = true;
        }
        if (expandToggle) expandToggle.hidden = true;

        if (panel && panel.classList.contains('is-open')) {
          panel.classList.remove('is-open');
          if (expandToggle) {
             expandToggle.setAttribute('aria-expanded', 'false');
             expandToggle.setAttribute('aria-label', this.expandLabel);
          }
          setTimeout(() => {
            if (!panel.classList.contains('is-open')) panel.hidden = true;
          }, 300);
        }
      }

      const selectGroup = this.querySelector(`[data-accessory-select-group][data-accessory-id="${rowId}"]`);
      const divider = this.querySelector(`[data-accessory-divider][data-accessory-id="${rowId}"]`);
      if (selectGroup) selectGroup.hidden = false;
      if (divider) divider.hidden = false;

      this.syncSelectAll();

      const priceWrap = this.querySelector(`[data-accessory-price][data-accessory-id="${rowId}"]`);
      if (!priceWrap) return;

      const priceInner = priceWrap.querySelector('.bundle-widget__price-inner');
      const currentEl = priceWrap.querySelector('.price__current .js-value') || priceWrap.querySelector('.js-value');
      const wasWrap = priceWrap.querySelector('.price__was');
      const wasEl = wasWrap ? wasWrap.querySelector('.js-value') : null;

      if (currentEl) {
        currentEl.textContent = this.formatMoney(variant.price);
      } else if (priceInner) {
        priceInner.textContent = this.formatMoney(variant.price);
      }

      const onSale = variant.compareAtPrice && variant.compareAtPrice > variant.price;
      if (wasWrap) {
        if (onSale && wasEl) {
          wasEl.textContent = this.formatMoney(variant.compareAtPrice);
          wasWrap.hidden = false;
          if (priceInner) priceInner.classList.add('price--on-sale');
        } else {
          wasWrap.hidden = true;
          if (priceInner) priceInner.classList.remove('price--on-sale');
        }
      }

      const discountedEl = priceWrap.querySelector('[data-accessory-discounted-price]');
      const priceContainer = priceWrap;

      if (this.hasDiscount) {
        priceContainer.classList.add('bundle-widget__price--has-discount');
        if (discountedEl) {
          const discounted = Math.round(variant.price * (1 - this.discountPercent / 100));
          discountedEl.textContent = this.formatMoney(discounted);
          discountedEl.hidden = false;
        }
      } else {
        priceContainer.classList.remove('bundle-widget__price--has-discount');
        if (discountedEl) {
          discountedEl.hidden = true;
        }
      }
    }

    formatMoney(cents) {
      if (window.Shopify && typeof Shopify.formatMoney === 'function') {
        try {
          return Shopify.formatMoney(cents, window.theme_money_format || '${{amount}}');
        } catch (e) {
          /* fall through */
        }
      }
      return '$' + (cents / 100).toFixed(2);
    }

    getCheckedAccessoryCount() {
      let count = 0;
      Object.values(this.accessories).forEach((accessory) => {
        const card = this.querySelector(`[data-accessory-card][data-accessory-id="${accessory.rowId}"]`);
        const group = card ? card.closest('[data-bundle-variant-group]') : null;
        const isVisible = group ? !group.hidden : true;

        if (isVisible && accessory.checked && accessory.currentVariant) {
          count++;
        }
      });
      return count;
    }

    recalculate() {
      const summaryEl = this.querySelector('[data-bundle-summary]');
      if (!summaryEl) return;

      if (!this.hasDiscount) {
        summaryEl.hidden = true;
        this.updateButtonLabels(this.getCheckedAccessoryCount());
        return;
      }

      let totalCents = 0;
      let saveCents = 0;
      let count = 0;

      Object.values(this.accessories).forEach((accessory) => {
        const card = this.querySelector(`[data-accessory-card][data-accessory-id="${accessory.rowId}"]`);
        const group = card ? card.closest('[data-bundle-variant-group]') : null;
        const isVisible = group ? !group.hidden : true;

        if (isVisible && accessory.checked && accessory.currentVariant) {
          const original = accessory.currentVariant.price;
          const discounted = Math.round(original * (1 - this.discountPercent / 100));
          totalCents += discounted;
          saveCents += original - discounted;
          count += 1;
        }
      });

      const countLabel = this.querySelector('[data-bundle-count-label]');
      const totalEl = this.querySelector('[data-bundle-total]');
      const saveEl = this.querySelector('[data-bundle-save]');
      const microcopyEl = this.querySelector('[data-bundle-microcopy]');

      summaryEl.hidden = count === 0;

      if (countLabel && this.summaryLabelTemplate) {
        countLabel.textContent = this.summaryLabelTemplate.replace('[count]', count);
      }
      if (totalEl) totalEl.textContent = this.formatMoney(totalCents);
      if (saveEl && this.saveTemplate) {
        saveEl.textContent = this.saveTemplate.replace('[amount]', this.formatMoney(saveCents));
      }
      if (microcopyEl) microcopyEl.textContent = this.checkoutMicrocopy;

      this.updateButtonLabels(count);
    }

    updateButtonLabels(count) {
      [this.mainAddBtn, this.stickyAddBtn].forEach((btn) => {
        if (!btn || btn.getAttribute('aria-disabled') === 'true') return;
        const base = btn.dataset.addToCartText;
        if (!base) return;
        btn.textContent = base;
      });

      if (
        this.buyNowBtn &&
        !this.buyNowBtn.disabled &&
        this.buyNowBaseText
      ) {
        this.buyNowBtn.textContent = this.buyNowBaseText;
      }
    }

    // ==========================================
    // DOM Teleportation for Mobile 3-Grid Layout
    // ==========================================
    setupMobileTeleport() {
      // 1. Only execute this logic on the 3-grid layout
      const is3Grid = document.querySelector('.product-3grid');
      if (!is3Grid) return;

      const widgetBlock = this.closest('.product-info__block');
      if (!widgetBlock) return;

      // 2. Create an invisible marker so we know exactly where to return it on Desktop
      const originalMarker = document.createElement('div');
      originalMarker.style.display = 'none';
      widgetBlock.parentNode.insertBefore(originalMarker, widgetBlock);

      // 3. Find the Buy Buttons block
      const productForm = document.querySelector('product-form');
      if (!productForm) return;
      
      const buyButtonsBlock = productForm.closest('.product-info__block');
      if (!buyButtonsBlock) return;

      // 4. Define the breakpoint (1024px catches mobile & tablets when 3-grid stacks)
      const mql = window.matchMedia('(max-width: 1024px)');

      const handleResize = (e) => {
        if (e.matches) {
          // Mobile view: Teleport widget directly above the Buy Buttons
          if (buyButtonsBlock.parentNode) {
            buyButtonsBlock.parentNode.insertBefore(widgetBlock, buyButtonsBlock);
          }
        } else {
          // Desktop view: Return widget to the middle column
          if (originalMarker.parentNode) {
            originalMarker.parentNode.insertBefore(widgetBlock, originalMarker);
          }
        }
      };

      // Listen for window resizes
      mql.addEventListener('change', handleResize);
      
      // Run once immediately on load
      handleResize(mql);
    }
  }

  if (!customElements.get('bundle-widget')) {
    customElements.define('bundle-widget', BundleWidget);
  }
})();